<template lang="pug">
  div
    v-radio-group(
      :value="pkFormType"
      :label="$t('Type of private key carrier')"
      class="eds-iit-checkbox__radio-group"
      :disabled="loading"
      hide-details
      @change="onPkFormTypeChange"
    )
      v-radio(
        :value="pkFormTypes.file"
        :ripple="false"
      )
        template(v-slot:label)
          div
            p(class="text-14px light dark mb-0") {{ $t('File carrier') }}
            span(class="text-12px light") ({{ $t('flash disk, CD disk, SD card, etc') }})
      v-radio(
        :value="pkFormTypes.KM"
        :ripple="false"
      )
        template(v-slot:label)
          div
            p(class="text-14px light dark mb-0") {{ $t('Protected carriers') }}
            span(class="text-12px light") ({{ $t('electronic key Almaz-1K, Crystal-1, Hryada-301, ID card, etc') }})
      v-radio(
        :value="pkFormTypes.KSP"
        :ripple="false"
      )
        template(v-slot:label)
          div
            p(class="text-14px light dark mb-0") Хмарний носій
    div(
      v-if="textError"
      v-html="textError"
      class="eds-iit-checkbox__text-error"
    )
    ValidationObserver(
      ref="form"
      slim
      v-else
    )
      v-form(@submit.prevent.stop="handleReadKey")
        div(v-if="isPkFormTypeKSP")
          ValidationProvider(
            rules="required"
            vid="selectedKSP"
            :name="wrapQuotesProviderName($t('Type of signature service'))"
            v-slot="{ errors }"
            mode="lazy"
          )
            v-select(
              v-model="selectedKSP"
              label="Тип сервісу підпису"
              :items="cloudProviders"
              item-text="name"
              item-value="name"
              filled
              :error-messages="errors"
              :menu-props="{ offsetY: true }"
            )
          ValidationProvider(
            v-if="!isQrCloudProvider"
            rules="required"
            vid="userIdKSP"
            :name="wrapQuotesProviderName($t('User identifier'))"
            v-slot="{ errors }"
            mode="lazy"
          )
            v-text-field(
              v-model="userIdKSP"
              label="Ідентифікатор користувача"
              :error-messages="errors"
              filled
            )
        div(v-else-if="isPkFormTypeFile || isPkFormTypeKM")
          v-select(
            v-model="ca"
            :label="$t('EDS service provider')"
            :items="acskProviders"
            class="eds-iit-checkbox__select"
            :no-data-text="$t('No data available')"
            filled
            hide-details
            :menu-props="{ offsetY: true }"
          )
          div(v-if="isPkFormTypeFile")
            block-key-file-input(
              ref="keyFileInput"
              :file-name="fileName"
              :file-selected="fileSelected"
              :file-error.sync="fileError"
            )
          div(v-else-if="isPkFormTypeKM")
            ValidationProvider(
              rules="required"
              vid="km"
              :name="wrapQuotesProviderName($t('Personal key carrier'))"
              v-slot="{ errors }"
              mode="passive"
            )
              v-select(
                v-model="km"
                :label="$t('Personal key carrier')"
                :items="keyMedias"
                class="mb-1"
                :error-messages="errors"
                :no-data-text="$t('No data available')"
                item-text="visibleName"
                item-value="visibleName"
                outlined
                :menu-props="{ offsetY: true }"
              )
          ValidationProvider(
            :rules="!stampData ? 'required' : ''"
            vid="keyPassword"
            :name="wrapQuotesProviderName($t('Enter password key'))"
            v-slot="{ errors }"
            mode="passive"
          )
            e-input-password(
              v-model="password"
              data-test="eds-iit-checkbox-password-input"
              :label="$t('Enter password key')"
              type="password"
              :error-messages="errors"
              hide-details="auto"
              filled
            )
          div(
            v-if="!showStampInput"
            class="eds-iit-checkbox__stamp-wrapper"
          )
            v-btn(
              text
              class="secondary-button text-btn"
              @click="showStampInput = true"
            )
              e-svg-icon plus-main
              span(class="pl-2") {{ $t('Add stamp') }}
          div(
            v-else
            class="pt-8"
          )
            block-key-file-input(
              ref="stampFileInput"
              :is-stamp="true"
              :file-name="stampName"
              :file-selected="stampSelected"
              :file-error.sync="stampError"
            )
            <!-- TODO: reuse -->
            ValidationProvider(
              :rules="stampSelected ? 'required' : ''"
              vid="stampPassword"
              :name="wrapQuotesProviderName($t('Enter password key (stamp)'))"
              v-slot="{ errors }"
              mode="passive"
            )
              e-input-password(
                v-model="stampPassword"
                :label="$t('Enter password key (stamp)')"
                type="password"
                :error-messages="errors"
                hide-details="auto"
                filled
              )
        div(
          class="two-buttons-wrapper"
          :class="{ 'offset-to-right': !haveSavedKeys, 'pt-8': showStampInput }"
        )
          v-btn(
            v-if="haveSavedKeys"
            height="48"
            @click.stop="$emit('changeStep', steps.savedKeys)"
          ) {{ $t('Saved keys') }}
          v-btn(
            class="main-button"
            data-test="eds-iit-checkbox-read-key-btn"
            :loading="loading || localLoading"
            type="submit"
            :disabled="readBtnDisabled"
          ) {{ $t('Read key') }}
</template>

<script>
import { EndUserKeyMedia } from 'euscp'
import { mapGetters } from 'vuex'
import validate from '~/mixins/validation/validate'
import EInputPassword from '~/components/elements/inputs/e-input-password'
import BlockKeyFileInput from '~/modules/eds-iit-checkbox/components/blocks/block-key-file-input'
import edsIitCheckboxSteps from '~/modules/eds-iit-checkbox/mixins/edsIitCheckboxSteps'

export default {
  name: 'EdsIitCheckboxForm',
  components: {
    BlockKeyFileInput,
    EInputPassword
  },
  mixins: [validate, edsIitCheckboxSteps],
  props: {
    endUser: {
      type: Object,
      default: () => ({})
    },
    pkFormType: {
      type: Number,
      default: 1
    },
    textError: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    pkFormTypes: {
      type: Object,
      default: () => ({})
    },
    isPkFormTypeFile: {
      type: Boolean,
      default: false
    },
    isPkFormTypeKM: {
      type: Boolean,
      default: false
    },
    isPkFormTypeKSP: {
      type: Boolean,
      default: false
    },
    initializeError: {
      type: Boolean,
      default: false
    },
    settings: {
      type: Object,
      default: () => ({})
    },
    haveSavedKeys: {
      type: Boolean,
      default: false
    },
    keyMedias: {
      type: Array,
      default: () => []
    },
    cloudProviders: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      userIdKSP: null,
      selectedKSP: null,
      localLoading: false,
      fileError: null,
      stampError: null,
      ca: null,
      km: null,
      password: null,
      stampPassword: null,
      showStampInput: false
    }
  },
  computed: {
    ...mapGetters('edsKeys', {
      fileData: 'uploadedKey',
      stampData: 'uploadedStamp'
    }),
    readBtnDisabled() {
      return Boolean(this.initializeError || this.fileError || this.stampError)
    },
    fileSelected() {
      return Boolean(this.fileError || this._.get(this.fileData, 'file'))
    },
    stampSelected() {
      return Boolean(this.stampError || this._.get(this.stampData, 'file'))
    },
    acskProviders() {
      const result = [
        {
          text: this.$t('Determine automatically'),
          value: null
        }
      ]
      this._.each(this.settings.CAs, ca => {
        const caCN = ca.issuerCNs[0]
        result.push({
          text: caCN,
          value: caCN
        })
      })
      return result
    },
    fileName() {
      return this._.get(this.fileData, 'file.name', null)
    },
    stampName() {
      return this._.get(this.stampData, 'file.name', null)
    },
    selectedKSPIndex() {
      return this._.findIndex(this.cloudProviders, cp => cp.name === this.selectedKSP)
    },
    isQrCloudProvider() {
      return this._.find(
        this.cloudProviders,
        cp => cp.name === this.selectedKSP && cp.confirmationURL
      )
    }
  },
  watch: {
    selectedKSP() {
      this.$emit('selectCloudProvider', this.selectedKSPIndex)
    }
  },
  created() {
    this.clearData()
  },
  methods: {
    onPkFormTypeChange(type) {
      this.$emit('update:pkFormType', type)
    },
    async handleReadKey() {
      try {
        this.localLoading = true
        this.fileError = null
        this.stampError = null
        const valid = await this.$refs?.form?.validate()
        const selectedFile = this.isPkFormTypeFile ? this.fileData || this.stampData : true
        if (!selectedFile) {
          this.fileError = 'The field "Choose EDS key" must have a value'
        }
        if (!valid || !selectedFile) {
          return
        }
        if (!this.endUser) {
          return
        }
        if (this.isPkFormTypeFile) {
          // TODO; reuse with handleReadEdsFileKey
          // saving stamp password because we can process key and stamp together
          this.$store.commit('edsKeys/SET_STAMP_PASSWORD', this.stampPassword)
          const isStampReading =
            (this._.get(this.stampData, 'file') && !this._.get(this.fileData, 'file')) || false
          this.$emit('readStamp', isStampReading)
          const keyData = isStampReading
            ? this._.get(this.stampData, 'data')
            : this._.get(this.fileData, 'data')
          const fileData = new Uint8Array(keyData)
          const isJKSkey = isStampReading
            ? this.stampName.endsWith('.jks')
            : this.fileName.endsWith('.jks')
          if (isJKSkey) {
            const jksKeys = await this.endUser.GetJKSPrivateKeys(fileData)
            const pkIndex = 0
            const pkCertificates = []
            for (let i = 0; i < jksKeys[pkIndex].certificates.length; i++) {
              pkCertificates.push(jksKeys[pkIndex].certificates[i].data)
            }
            await this.endUser.ReadPrivateKeyBinary(
              jksKeys[pkIndex].privateKey,
              isStampReading ? this.stampPassword : this.password,
              pkCertificates,
              this.ca
            )
          } else {
            await this.endUser.ReadPrivateKeyBinary(
              fileData,
              isStampReading ? this.stampPassword : this.password,
              null,
              this.ca
            )
          }
        } else if (this.isPkFormTypeKM) {
          const selectedKeyMedia = this._.find(this.keyMedias, { visibleName: this.km })
          const keyMedia = new EndUserKeyMedia(selectedKeyMedia)
          keyMedia.password = this.password
          await this.endUser.ReadPrivateKey(keyMedia, null, this.ca)
        } else if (this.isPkFormTypeKSP) {
          if (this.isQrCloudProvider) {
            this.$emit('handleQrCloudSignature', this.selectedKSP)
            return
          } else {
            await this.endUser.ReadPrivateKeyKSP(this.userIdKSP, this.selectedKSP, true)
          }
        }
        const localKeyData = await this.endUser.GetOwnCertificates()
        this.$emit('getLocalKeyData', localKeyData)
        this.$emit('changeStep', this.steps.keyInfo)
        this.$emit('resetModelData')
      } catch (e) {
        if (e?.code !== 12) {
          this.$handlers.error(e, this)
        }
        this.$emit('resetLocalData')
      } finally {
        this.localLoading = false
      }
    },
    clearData() {
      const form = this.$refs?.form
      if (form) {
        form.reset()
      }
      this.password = null
      this.stampPassword = null
      this.ca = null
      this.km = null
      this.fileError = null
      this.stampError = null
      this.userIdKSP = null
      this.selectedKSP = null
      this.showStampInput = false
      this.resetKeyAndStampData()
      this.updateFileInputKeys()
    },
    resetKeyAndStampData() {
      this.$store.commit('edsKeys/SET_EDS_KEY', null)
      this.$store.commit('edsKeys/SET_EDS_STAMP', null)
      this.$store.commit('edsKeys/SET_STAMP_PASSWORD', null)
    },
    updateFileInputKeys() {
      if (this.$refs.keyFileInput) {
        this.$refs.keyFileInput.updateFileInputKey()
      }
      if (this.$refs.stampFileInput) {
        this.$refs.stampFileInput.updateFileInputKey()
      }
    }
  }
}
</script>

<style scoped lang="scss"></style>
